import { MessageSeverity } from "@sinch/core/entities/serverEnums";

import { FORM_ERRORS_FIELD } from "@sinch/hooks/useMuiForm";
import type { MutationMessage } from "@sinch/types/sinch.types";

import { isEmpty, isNil, length, values } from "ramda";
import { isString } from "ramda-adjunct";
import { FieldValues, UseFormReturn } from "react-hook-form";

export const applyErrorToForm =
  (form: UseFormReturn, type?: string) =>
  ({ field, message, severity }: MutationMessage) => {
    if (severity !== MessageSeverity.Error) return;
    const fieldKey = isNil(field) || isEmpty(field) ? FORM_ERRORS_FIELD : field;
    const { error } = form.getFieldState(fieldKey);
    const currentErrors = error?.types || {};
    const currentErrorsCount = length(values(currentErrors));

    form.setError(
      fieldKey,
      {
        type: type ?? "custom",
        message: ((error?.message ?? "") + "\n" + message).trim(),
        // TODO: Add error keys from server
        types: { ...currentErrors, [`${type ?? "custom"}${currentErrorsCount + 1}`]: message },
      },
      { shouldFocus: true }
    );
  };

/**
 * Extracts error messages from a nested object structure. This function traverses through
 * each key-value pair of the provided object, recursively searching for any nested fields
 * with a key named "message" and a string value. All found string messages are collected
 * into an string array and returned.
 */
export const collectErrorMessagesFromFields = <TFieldValues extends FieldValues>(fields: TFieldValues): string[] => {
  const errorMessages: string[] = [];
  for (const [key, val] of Object.entries(fields ?? {})) {
    const isMessageField = key === "message" && isString(val);
    const isObject = val && typeof val === "object";

    if (isMessageField) {
      errorMessages.push(val as string);
      continue;
    }
    if (isObject) {
      errorMessages.push(...collectErrorMessagesFromFields(val as TFieldValues));
    }
  }
  return errorMessages;
};
